/**
 * ViewModeSlider - Segmented Toggle Slider for View Mode Selection
 *
 * Replaces the existing range slider with a modern segmented toggle design.
 * Supports Single / Split / Grid view modes with directional animation.
 *
 * Gated by: NEW_DESIGN_BETA flag
 * Rollback: Set flag to false to restore legacy slider
 */

import React, { useEffect } from 'react';
import type { DensityOption } from './state/density';
import { logger } from '../../lib/logging';
import { SplitViewIcon } from '../icons/SplitViewIcon';

export interface ViewModeSliderProps {
  value: DensityOption;
  onChange: (mode: DensityOption) => void;
  className?: string;
  collapsed?: boolean;
}

type ViewModeOption = {
  value: DensityOption;
  label: string;
  icon: React.ReactNode;
};

const VIEW_MODE_OPTIONS: ViewModeOption[] = [
  {
    value: 'stacked',
    label: 'Single',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="7" width="16" height="10" rx="2" strokeWidth={2} />
      </svg>
    ),
  },
  {
    value: 'comfortable',
    label: 'Split',
    icon: <SplitViewIcon />,
  },
  {
    value: 'compact',
    label: 'Grid',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="4" width="7" height="7" rx="2" strokeWidth={2} />
        <rect x="13" y="4" width="7" height="7" rx="2" strokeWidth={2} />
        <rect x="4" y="13" width="7" height="7" rx="2" strokeWidth={2} />
        <rect x="13" y="13" width="7" height="7" rx="2" strokeWidth={2} />
      </svg>
    ),
  },
];

export const ViewModeSlider: React.FC<ViewModeSliderProps> = ({
  value,
  onChange,
  className = '',
  collapsed = false,
}) => {
  // Log component mount with initial value
  useEffect(() => {
    logger.info('view_mode_slider_mounted', undefined, {
      op: 'view_mode_slider_mounted',
      component: 'ViewModeSlider',
      initialValue: value,
    });
  }, []);

  const handleSelect = (mode: DensityOption) => {
    const startTime = performance.now();
    const correlationId = crypto.randomUUID();

    try {
      onChange(mode); // This triggers useDensity's setDensity with localStorage + URL hash persistence

      logger.info('view_mode_slider_select', correlationId, {
        op: 'view_mode_slider_select',
        component: 'ViewModeSlider',
        viewMode: mode,
        correlationId,
        duration_ms: performance.now() - startTime,
        success: true,
        persisted: true, // Confirmed: persistence happens via useDensity hook
      });
    } catch (error) {
      logger.error('view_mode_slider_select', correlationId, {
        op: 'view_mode_slider_select',
        component: 'ViewModeSlider',
        viewMode: mode,
        correlationId,
        duration_ms: performance.now() - startTime,
        success: false,
        err_message: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  };

  const handleHover = (mode: DensityOption, isEntering: boolean) => {
    logger.info('view_mode_slider_hover', undefined, {
      op: 'view_mode_slider_hover',
      component: 'ViewModeSlider',
      viewMode: mode,
      action: isEntering ? 'enter' : 'leave',
      currentSelection: value,
    });
  };

  const handleTransitionEnd = (mode: DensityOption) => {
    logger.info('view_mode_slider_transition_end', undefined, {
      op: 'view_mode_slider_transition_end',
      component: 'ViewModeSlider',
      viewMode: mode,
      isSelected: value === mode,
    });
  };

  if (collapsed) {
    return (
      <div className={`${className} flex flex-col items-center gap-2`} role="tablist" aria-label="View mode selector">
        {/* Eye icon - non-clickable indicator */}
        <div className="text-slate-400 mb-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </div>
        {/* View mode buttons - vertical layout */}
        <div className="flex flex-col items-center gap-2">
          {VIEW_MODE_OPTIONS.map((option) => {
            const isSelected = value === option.value;

            return (
              <button
                key={option.value}
                role="tab"
                aria-selected={isSelected}
                aria-label={`${option.label} view`}
                title={`${option.label} view`}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => handleHover(option.value, true)}
                onMouseLeave={() => handleHover(option.value, false)}
                onTransitionEnd={() => handleTransitionEnd(option.value)}
                className={`
                  w-10 h-10 p-2 rounded-lg flex items-center justify-center
                  transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800
                  ${
                    isSelected
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/50'
                      : 'bg-slate-700/30 border border-slate-600/50 text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }
                `}
              >
                <span className={isSelected ? 'text-cyan-400' : 'text-slate-500'}>
                  {option.icon}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={className} role="tablist" aria-label="View mode selector">
      {/* Segmented Toggle Buttons - Icons Only */}
      <div className="flex gap-1 p-1 bg-slate-700/30 rounded-lg">
        {VIEW_MODE_OPTIONS.map((option, index) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              role="tab"
              aria-selected={isSelected}
              aria-label={`${option.label} view`}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => handleHover(option.value, true)}
              onMouseLeave={() => handleHover(option.value, false)}
              onTransitionEnd={() => handleTransitionEnd(option.value)}
              className={`
                flex-1 flex items-center justify-center px-3 py-2 rounded-md
                font-medium transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800
                ${
                  isSelected
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/50 shadow-sm shadow-cyan-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }
              `}
            >
              <span className={isSelected ? 'text-cyan-400' : 'text-slate-500'}>
                {option.icon}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
